// utils/formatters.js
// All formatting functions extracted from Profile.js

import { ACTIVITY_TYPE_MAP } from './constants';

export const formatDate = (ts) => {
  if (!ts || isNaN(ts)) return "Not set";
  if (ts > 1e12) ts = Math.floor(ts / 1000);
  if (ts < 946684800 || ts > 4102444800) return "Not set";
  return new Date(ts * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatRemainingTime = (remainingSeconds) => {
  if (!remainingSeconds || remainingSeconds <= 0) return "Released";
  const days = Math.floor(remainingSeconds / 86400);
  const hours = Math.floor((remainingSeconds % 86400) / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  return `${days} days, ${hours} hrs, ${minutes} mins`;
};

export const formatTokenAmount = (raw, tokenDecimals = 18, displayDecimals = 2) => {
  if (!raw || isNaN(raw)) return "0.00";
  const value = Number(raw) / 10 ** tokenDecimals;
  if (value >= 1e9) return `${(value / 1e9).toFixed(displayDecimals)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(displayDecimals)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(displayDecimals)}K`;
  return value.toFixed(displayDecimals);
};

export const formatNumber = (num, decimals = 0) => {
  if (!num || isNaN(num)) return "0";
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

export const getTitleFromPoints = (points) => {
  if (points >= 100000) return "Eternal Architect";
  if (points >= 50000) return "Reality Weaver";
  if (points >= 25000) return "Quantum Voyager";
  if (points >= 15000) return "Arweave Pathfinder";
  if (points >= 5000) return "Nebula Scribe";
  if (points >= 10000) return "Protocol Ascendant";
  if (points >= 2500) return "Initiate of Light";
  return "Digital Wanderer";
};

// Using the ACTIVITY_TYPE_MAP constant
export const getActivityDisplayName = (activityType) => {
  return ACTIVITY_TYPE_MAP[activityType] || activityType || 'Unknown Activity';
};

export const buildKeywords = (u) => {
  const cosmicRolePattern = /(Holder|Verified|amVerified)$/i;
  const cosmicRoles = Array.isArray(u.roles)
    ? u.roles
        .filter((r) => cosmicRolePattern.test(r))
        .map((r) => `role-${r.toLowerCase().replace(/\s+/g, "-")}`)
    : [];

  const raw = [
    `discord-${u.userId}`,
    `process-${u.processId}`,
    u.spawnGuildId ? `spawn-guild-${u.spawnGuildId}` : "",
    ...cosmicRoles,
    u.joinDate ? `join-${formatDate(u.joinDate).replace(/\s+/g, "-")}` : "",
    u.ogSender ? `og-sender-${u.ogSender}` : "",
    `x-posts-${u.xPostingsCount}`,
    `gh-interactions-${u.githubInteractionsCount}`,
    `ampoints-${u.points}`,
    `gm-streak-${u.streaks?.gm?.total || 0}`,
    `gn-streak-${u.streaks?.gn?.total || 0}`,
  ]
    .filter(Boolean)
    .map((k) =>
      k
        .toString()
        .replace(/[^a-z0-9-]/gi, "-")
        .toLowerCase()
        .slice(0, 50)
    );

  return [...new Set(raw)].slice(0, 16);
};

export const normalizeGuildRoles = (rolesObj) => {
  if (!rolesObj || typeof rolesObj !== 'object') return {};
  const normalized = {};
  for (const [guildId, roles] of Object.entries(rolesObj)) {
    if (Array.isArray(roles)) {
      normalized[guildId] = [...new Set(roles)];
    } else if (typeof roles === 'string') {
      normalized[guildId] = [roles];
    } else {
      normalized[guildId] = [];
    }
  }
  return normalized;
};
// 1+
// Digital Wanderer
// You’ve just entered the grid - seeking, observing, learning. A soul in search of signal.
// 2,500+
// Initiate of Light
// You’ve begun to interact meaningfully. A spark in the system, noticed by the network.
// 5,000+
// Nebula Scribe
// Your actions echo. Trails of presence now persist in the protocol.
// 10,000+
// Protocol Ascendant
// Your amPassport is minted. Reputation is no longer optional — it’s on-chain.
// 15,000+
// Arweave Pathfinder
// You navigate with intention through the fabric of permanence. A guide to others.
// 25,000+
// Quantum Voyager
// You operate across layers — code, identity, presence. A traveler of parallel truths.
// 50,000+
// Reality Weaver
// You interlace your influence into the Permaweb itself — threads of culture and code.
// 100,000+
// Eternal Architect
// You don’t just exist in the system — you helped define its structure. Legendary.
